import { Check } from 'lucide-react';
import { useState } from 'react';
import { cn } from '../../../lib/utils';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../ui/command';
import { Popover, PopoverContent } from '../../ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import type { FieldControlProps } from '../field-control';
import type { SchemaFormOption } from '../types';
import { PickerChip, PickerShell } from './async-picker';

const NONE = '__none__';

/** `single_select`: Radix select over the active options; "None" clears when not required. */
export function SingleSelectControl(props: FieldControlProps) {
  const { field, value, onChange, id, disabled, invalid, describedBy } = props;
  const options = field.options ?? [];
  const current = typeof value === 'string' ? value : '';
  const orphan = current !== '' && !options.some((o) => o.id === current);
  return (
    <Select
      value={current}
      onValueChange={(next) => onChange(next === NONE ? undefined : next)}
      disabled={disabled || field.readOnly}
    >
      <SelectTrigger
        id={id}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        aria-required={field.required || undefined}
      >
        <SelectValue placeholder={field.placeholder ?? 'Select…'} />
      </SelectTrigger>
      <SelectContent>
        {field.required ? null : (
          <SelectItem value={NONE}>
            <span className="text-fg-muted">None</span>
          </SelectItem>
        )}
        {options.map((o) => (
          <SelectItem key={o.id} value={o.id}>
            <OptionLabel option={o} />
          </SelectItem>
        ))}
        {orphan ? (
          <SelectItem value={current} disabled>
            <span className="text-fg-muted italic">Archived option</span>
          </SelectItem>
        ) : null}
      </SelectContent>
    </Select>
  );
}

function OptionLabel({ option }: { option: SchemaFormOption }) {
  return (
    <span className="inline-flex items-center gap-2">
      {option.color ? (
        <span
          aria-hidden="true"
          className="size-2.5 rounded-full"
          style={{ backgroundColor: option.color }}
        />
      ) : null}
      {option.label}
    </span>
  );
}

/** `multi_select`: popover + searchable list with check toggles; selection shown as badges. */
export function MultiSelectControl(props: FieldControlProps) {
  const { field, value, onChange, id, disabled, invalid, describedBy } = props;
  const [open, setOpen] = useState(false);
  const options = field.options ?? [];
  const selected = Array.isArray(value) ? (value as string[]) : [];
  const max = field.config?.type === 'multi_select' ? field.config.maxSelections : undefined;
  const labelOf = (optionId: string) => options.find((o) => o.id === optionId);
  const readOnly = disabled || field.readOnly;

  const toggle = (optionId: string) => {
    if (selected.includes(optionId)) {
      const next = selected.filter((v) => v !== optionId);
      onChange(next.length === 0 ? undefined : next);
    } else {
      if (max !== undefined && selected.length >= max) return;
      onChange([...selected, optionId]);
    }
  };

  return (
    <Popover open={open} onOpenChange={readOnly ? undefined : setOpen}>
      <PickerShell
        id={id}
        open={open}
        disabled={readOnly}
        invalid={invalid}
        required={field.required}
        describedBy={describedBy}
        buttonLabel={
          selected.length === 0 ? (
            <span className="text-fg-muted">{field.placeholder ?? 'Select…'}</span>
          ) : (
            <span className="text-fg-muted">Add…</span>
          )
        }
      >
        {selected.map((optionId) => {
          const option = labelOf(optionId);
          return (
            <PickerChip
              key={optionId}
              label={option?.label ?? 'Archived option'}
              color={option?.color}
              onRemove={readOnly ? undefined : () => toggle(optionId)}
            />
          );
        })}
      </PickerShell>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
        <Command>
          <CommandInput placeholder="Filter options…" />
          <CommandList>
            <CommandEmpty>No options.</CommandEmpty>
            <CommandGroup>
              {options.map((o) => {
                const checked = selected.includes(o.id);
                return (
                  <CommandItem
                    key={o.id}
                    value={`${o.label} ${o.id}`}
                    onSelect={() => toggle(o.id)}
                    aria-selected={checked}
                  >
                    <Check className={cn('size-4', checked ? 'opacity-100' : 'opacity-0')} />
                    <OptionLabel option={o} />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
